import * as React from 'react'
import { styled, createTheme, ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import MuiDrawer from '@mui/material/Drawer'
import Box from '@mui/material/Box'
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import List from '@mui/material/List'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Container from '@mui/material/Container'
import MenuIcon from '@mui/icons-material/Menu'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import PeopleIcon from '@mui/icons-material/People'
import BarChartIcon from '@mui/icons-material/BarChart'
import LayersIcon from '@mui/icons-material/Layers'
import { useSelector } from 'react-redux'
import { UserMenu } from './UserMenu'
import { State } from '../types/state'
import { useNavigate } from 'react-router'
import { ProfileContainer } from '../user/Profile'
import Paper from '@mui/material/Paper'
import { useState, MouseEvent } from 'react'
import Menu from '@mui/material/Menu'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import AdbIcon from '@mui/icons-material/Adb'

const drawerWidth: number = 240

interface AppBarProps extends MuiAppBarProps {
  open?: boolean
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: prop => prop !== 'open'
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  ...(open === true && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  })
}))

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: prop => prop !== 'open'
})(({ theme, open }) => ({
  '& .MuiDrawer-paper': {
    position: 'relative',
    whiteSpace: 'nowrap',
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    }),
    boxSizing: 'border-box',
    ...(open !== true && {
      overflowX: 'hidden',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
      }),
      width: theme.spacing(7),
      [theme.breakpoints.up('sm')]: {
        width: theme.spacing(9)
      }
    })
  }
}))

const defaultTheme = createTheme({
  palette: {
    primary: {
      main: '#667eea',
      dark: '#764ba2'
    },
    secondary: {
      main: '#f6ad55'
    }
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    h6: {
      fontWeight: 600
    }
  },
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: 'linear-gradient(180deg, #ffffff 0%, #f7fafc 100%)',
          borderRight: '1px solid rgba(102, 126, 234, 0.1)'
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)'
        }
      }
    }
  }
})

export function HeaderNormal ({ children }: any): JSX.Element {
  const { username } = useSelector((state: State) => state.user)
  const { isSpinnerVisible } = useSelector((state: State) => state.refresh)

  const [open, setOpen] = React.useState(true)
  const toggleDrawer = (): void => {
    setOpen(!open)
  }
  if (username === null) {
    return <>{children}</>
  }
  if (isSpinnerVisible) {
    return <></>
  }
  return (
    <ThemeProvider theme={defaultTheme}>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar position='absolute' open={open}>
          <Toolbar
            sx={{
              pr: '24px'
            }}
          >
            <IconButton
              edge='start'
              color='inherit'
              aria-label='open drawer'
              onClick={toggleDrawer}
              sx={{
                marginRight: '36px',
                ...(open && { display: 'none' })
              }}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              component='h1'
              variant='h6'
              color='inherit'
              noWrap
              sx={{ flexGrow: 1 }}
            >
              VReMa
            </Typography>
            {username !== null && <UserMenu username={username} />}
          </Toolbar>
        </AppBar>
        <Drawer variant='permanent' open={open}>
          <Toolbar
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              px: [1]
            }}
          >
            <IconButton onClick={toggleDrawer}>
              <ChevronLeftIcon />
            </IconButton>
          </Toolbar>
          <Divider />
          <List component='nav'>
            <MainListItems />
          </List>
        </Drawer>
        <Box
          component='main'
          sx={{
            backgroundColor: theme =>
              theme.palette.mode === 'light'
                ? theme.palette.grey[100]
                : theme.palette.grey[900],
            flexGrow: 1,
            height: '100vh',
            overflow: 'auto'
          }}
        >
          <Toolbar />
          <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: 3 }}>
            <ProfileContainer component={Paper}>{children}</ProfileContainer>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  )
}

export const MainListItems = (): JSX.Element => {
  const navigate = useNavigate()

  return (
    <>
      <ListItemButton
        onClick={() => navigate('statistics')}
        sx={{
          borderRadius: '12px',
          margin: '4px 8px',
          '&:hover': {
            backgroundColor: 'rgba(102, 126, 234, 0.1)',
            transform: 'translateX(4px)',
            transition: 'all 0.3s ease'
          }
        }}
      >
        <ListItemIcon>
          <BarChartIcon sx={{ color: '#667eea' }} />
        </ListItemIcon>
        <ListItemText
          primary='Statistics'
          primaryTypographyProps={{ fontWeight: 500 }}
        />
      </ListItemButton>
      <ListItemButton
        onClick={() => navigate('contracts')}
        sx={{
          borderRadius: '12px',
          margin: '4px 8px',
          '&:hover': {
            backgroundColor: 'rgba(102, 126, 234, 0.1)',
            transform: 'translateX(4px)',
            transition: 'all 0.3s ease'
          }
        }}
      >
        <ListItemIcon>
          <LayersIcon sx={{ color: '#667eea' }} />
        </ListItemIcon>
        <ListItemText
          primary='Contracts'
          primaryTypographyProps={{ fontWeight: 500 }}
        />
      </ListItemButton>
      <ListItemButton
        onClick={() => navigate('contractors')}
        sx={{
          borderRadius: '12px',
          margin: '4px 8px',
          '&:hover': {
            backgroundColor: 'rgba(102, 126, 234, 0.1)',
            transform: 'translateX(4px)',
            transition: 'all 0.3s ease'
          }
        }}
      >
        <ListItemIcon>
          <PeopleIcon sx={{ color: '#667eea' }} />
        </ListItemIcon>
        <ListItemText
          primary='Contractors'
          primaryTypographyProps={{ fontWeight: 500 }}
        />
      </ListItemButton>
    </>
  )
}

const USER_PAGES = ['statistics', 'contractors', 'contracts']

export const HeaderMobile = ({ children }: any): JSX.Element => {
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null)
  const navigate = useNavigate()
  const { username } = useSelector((state: State) => state.user)
  const { isSpinnerVisible } = useSelector((state: State) => state.refresh)

  const handleOpenNavMenu = (event: MouseEvent<HTMLElement>): void => {
    setAnchorElNav(event.currentTarget)
  }

  const handleCloseNavMenu = (): void => {
    setAnchorElNav(null)
  }

  const handleOpenPage = (option: string): void => {
    navigate(option)
  }

  if (username === null) {
    return <>{children}</>
  }
  if (isSpinnerVisible) {
    return <></>
  }

  return (
    <>
      <AppBar position='static' style={{ backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Container maxWidth='xl'>
          <CssBaseline />
          <Toolbar>
            <AdbIcon sx={{ display: { xs: 'none', md: 'flex' }, mr: 1 }} />
            <Typography
              variant='h6'
              component='div'
              onClick={() => handleOpenPage('/')}
              sx={{
                cursor: 'pointer',
                mr: 2,
                display: { xs: 'none', md: 'flex' },
                color: 'inherit',
                textDecoration: 'none',
                fontWeight: 700,
                letterSpacing: '0.05em',
                '&:hover': {
                  transform: 'scale(1.05)',
                  transition: 'transform 0.2s ease'
                }
              }}
            >
              VReMa
            </Typography>

            <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                size='large'
                onClick={handleOpenNavMenu}
                color='inherit'
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id='menu-appbar'
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left'
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left'
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: { xs: 'block', md: 'none' }
                }}
              >
                {username !== null &&
                  USER_PAGES.map(page => (
                    <MenuItem
                      key={page}
                      onClick={() => {
                        handleCloseNavMenu()
                        handleOpenPage(page)
                      }}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(102, 126, 234, 0.1)'
                        }
                      }}
                    >
                      <Typography textAlign='center' sx={{ textTransform: 'capitalize' }}>
                        {page}
                      </Typography>
                    </MenuItem>
                  ))}
                {username === null && (
                  <MenuItem
                    key='Login'
                    onClick={() => {
                      handleCloseNavMenu()
                      handleOpenPage('login')
                    }}
                  >
                    <Typography textAlign='center'>Login</Typography>
                  </MenuItem>
                )}
              </Menu>
            </Box>
            <Typography
              variant='h5'
              component='div'
              onClick={() => handleOpenPage('/')}
              sx={{
                cursor: 'pointer',
                mr: 2,
                display: { xs: 'flex', md: 'none' },
                flexGrow: 1,
                color: 'inherit',
                textDecoration: 'none',
                fontWeight: 700
              }}
            >
              VReMa
            </Typography>
            <Box
              sx={{
                flexGrow: 1,
                display: { xs: 'none', md: 'flex', marginLeft: '2vw' }
              }}
            >
              {username !== null &&
                USER_PAGES.map(page => (
                  <Button
                    key={page}
                    onClick={() => handleOpenPage(page)}
                    sx={{
                      my: 2,
                      color: 'white',
                      display: 'block',
                      textTransform: 'capitalize',
                      fontWeight: 600,
                      fontSize: '1rem',
                      borderRadius: '8px',
                      padding: '8px 20px',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    {page}
                  </Button>
                ))}
              {username === null && (
                <Button
                  key='Login'
                  onClick={() => handleOpenPage('login')}
                  sx={{
                    my: 2,
                    color: 'white',
                    display: 'block',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.15)'
                    }
                  }}
                >
                  Login
                </Button>
              )}
            </Box>
            {username !== null && <UserMenu username={username} />}
          </Toolbar>
        </Container>
      </AppBar>
      {children}
    </>
  )
}
